// ============================================
// Practice Session Engine - Motor de Sesiones Guiadas
// ============================================
// Una sesión es un plan de práctica con múltiples ejercicios
// que el estudiante recorre secuencialmente.
// ============================================

import {
  type ExerciseDef,
  type ExerciseState,
  type ExerciseCategory,
  type ExerciseDifficulty,
  type CheckResult,
  EXERCISES,
  generateWhatNoteState,
  generateFindNoteState,
  generateScaleClimbState,
} from "./exerciseEngine";

import {
  generateCagedPatterns,
  findPatternConnections,
  CAGED_DEFINITIONS,
  type CagedPattern,
} from "./cagedEngine";

import {
  generateScaleFingerings,
  buildBoxConnectionSequence,
  type FretboardPattern,
} from "./fingeringEngine";

import {
  buildScalePattern,
  type NoteName,
  type NeckPosition,
} from "./guitarEngine";

// ─── Tipos ───

export interface SessionExercise {
  /** ID único dentro de la sesión */
  stepId: string;
  /** Título corto */
  title: string;
  /** Descripción motivacional */
  description: string;
  /** El ejercicio base (puede ser uno de EXERCISES o uno generado) */
  exerciseDef: ExerciseDef;
  /** Estado generado del ejercicio */
  exerciseState: ExerciseState;
  /** Opcional: Si el ejercicio tiene secuencia animada */
  sequence?: NeckPosition[];
  /** Duración estimada en segundos */
  estimatedDurationSec: number;
  /** Tipo de entrenamiento */
  focus: "note" | "chord" | "scale" | "technique" | "caged";
  /** Objetivo pedagógico específico */
  goal: string;
  /** Consejo motivacional */
  tip: string;
}

export interface PracticeSessionPlan {
  /** ID único */
  id: string;
  /** Título de la sesión */
  title: string;
  /** Descripción general */
  description: string;
  /** Nivel */
  level: "principiante" | "intermedio" | "avanzado";
  /** Ejercicios en orden */
  exercises: SessionExercise[];
  /** Duración total estimada (minutos) */
  totalMinutes: number;
  /** Emoji representativo */
  emoji: string;
  /** Consejo final */
  closingTip: string;
  /** Firebase code si fue creado como tarea */
  taskCode?: string;
}

export interface SessionProgress {
  /** ID del plan */
  planId: string;
  /** Índice del ejercicio actual (0-based) */
  currentIndex: number;
  /** Ejercicios completados (stepId → resultado) */
  completed: Record<string, SessionExerciseResult>;
  /** Tiempo total practicado en ms */
  totalPracticeMs: number;
  /** Cuándo empezó */
  startedAt: number;
  /** Cuándo terminó (si completó) */
  completedAt?: number;
  /** Estrellas totales acumuladas */
  totalStars: number;
  /** Puntaje total */
  totalScore: number;
}

export interface SessionExerciseResult {
  stepId: string;
  completed: boolean;
  stars: number;
  score: number;
  attempts: number;
  timeMs: number;
  feedback?: string;
}

// ─── Generadores de Sesiones ───

const ALL_NOTES: NoteName[] = ["C", "D", "E", "F", "G", "A", "B"];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Genera un plan de práctica para principiantes.
 * Se enfoca en: notas en el mástil + calentamiento básico.
 * @param rootNote Si se indica (ej. "C" en el camino), what-note / find-note / scale-climb usan esa tónica.
 */
export function generateBeginnerSession(rootNote?: NoteName): PracticeSessionPlan {
  const exercises: SessionExercise[] = [];
  const forcedRoot =
    rootNote && ALL_NOTES.includes(rootNote) ? rootNote : undefined;

  // 1. Calentamiento 1-2-3-4
  const warmupDef = EXERCISES.find((e) => e.id === "warmup-1234")!;
  const warmupState = warmupDef.generate();
  exercises.push({
    stepId: "warmup",
    title: "🔥 Calentamiento",
    description: "Prepara tus dedos con el clásico 1-2-3-4",
    exerciseDef: warmupDef,
    exerciseState: warmupState,
    sequence: warmupState.sequence,
    estimatedDurationSec: 120,
    focus: "technique",
    goal: "Activar todos los dedos de la mano izquierda",
    tip: "Ve despacio al principio. La velocidad llega sola.",
  });

  // 2. ¿Qué nota es esta? (quiz)
  const noteQuizDef = EXERCISES.find((e) => e.id === "what-note")!;
  const noteQuizState = generateWhatNoteState(forcedRoot);
  exercises.push({
    stepId: "note-quiz",
    title: "🎯 ¿Qué nota es esta?",
    description: forcedRoot
      ? `Identifica la nota ${forcedRoot} (Do) en el mástil`
      : "Identifica una nota marcada en el mástil",
    exerciseDef: noteQuizDef,
    exerciseState: noteQuizState,
    estimatedDurationSec: 60,
    focus: "note",
    goal: "Asociar posición visual con nombre de nota",
    tip: "Cada cuerda tiene un nombre: 6=Mi, 5=La, 4=Re, 3=Sol, 2=Si, 1=Mi",
  });

  // 3. Encuentra la nota
  const findNoteDef = EXERCISES.find((e) => e.id === "find-note")!;
  const findNoteState = generateFindNoteState(forcedRoot);
  exercises.push({
    stepId: "find-note",
    title: "🔍 Encuentra la nota",
    description: forcedRoot
      ? `Busca todas las posiciones de Do (${forcedRoot})`
      : `Busca todas las posiciones de la nota indicada`,
    exerciseDef: findNoteDef,
    exerciseState: findNoteState,
    estimatedDurationSec: 90,
    focus: "note",
    goal: "Explorar el mástil encontrando una nota específica",
    tip: "Pista: busca la nota en diferentes cuerdas y trastes",
  });

  // 4. Sube la escala Mayor
  const scaleDef = EXERCISES.find((e) => e.id === "scale-climb")!;
  const scaleState = generateScaleClimbState(forcedRoot);
  exercises.push({
    stepId: "scale-climb",
    title: "🪜 Sube la escala",
    description: forcedRoot
      ? "Toca la escala de Do Mayor nota por nota"
      : "Toca la escala Mayor nota por nota",
    exerciseDef: scaleDef,
    exerciseState: scaleState,
    sequence: scaleState.sequence,
    estimatedDurationSec: 120,
    focus: "scale",
    goal: "Aprender la estructura de la escala Mayor",
    tip: "La escala Mayor tiene 7 notas. Fíjate en los patrones de tonos y semitonos.",
  });

  return {
    id: forcedRoot ? `beginner-${forcedRoot.toLowerCase()}` : "beginner-default",
    title: forcedRoot ? "🌱 Práctica en Do Mayor" : "🌱 Primera práctica",
    description: forcedRoot
      ? "Sesión del camino: todo en Do Mayor, preparando tu primera melodía."
      : "Perfecta para empezar: notas, escalas y técnica básica",
    level: "principiante",
    exercises,
    totalMinutes: Math.ceil(
      exercises.reduce((sum, e) => sum + e.estimatedDurationSec, 0) / 60
    ),
    emoji: "🌱",
    closingTip: forcedRoot
      ? "¡Listo! Ya conoces Do Mayor: sigue a Estrellita en el camino."
      : "¡Buen trabajo! Practica 10 minutos al día y verás resultados rápidos.",
  };
}

/**
 * Genera un plan de práctica intermedio.
 * Se enfoca en: escalas + patrones CAGED + conexiones.
 */
export function generateIntermediateSession(
  root: NoteName = "C",
  scaleType: string = "major"
): PracticeSessionPlan {
  const exercises: SessionExercise[] = [];
  const cagedPatterns = generateCagedPatterns(root, scaleType);
  const connections = findPatternConnections(cagedPatterns);

  // 1. Calentamiento spider
  const spiderDef = EXERCISES.find((e) => e.id === "spider")!;
  const spiderState = spiderDef.generate();
  exercises.push({
    stepId: "spider-warmup",
    title: "🕷️ Calentamiento araña",
    description: "Patrón 1-3-2-4 para independencia de dedos",
    exerciseDef: spiderDef,
    exerciseState: spiderState,
    sequence: spiderState.sequence,
    estimatedDurationSec: 120,
    focus: "technique",
    goal: "Independencia y coordinación de dedos",
    tip: "Mantén los dedos cerca de las cuerdas, no los levantes de más.",
  });

  // 2. Sube y baja la escala
  const upDownDef = EXERCISES.find((e) => e.id === "scale-updown")!;
  const upDownState = upDownDef.generate();
  exercises.push({
    stepId: "scale-updown",
    title: "↕️ Sube y baja la escala",
    description: `Escala de ${root} Mayor, subiendo y bajando`,
    exerciseDef: upDownDef,
    exerciseState: upDownState,
    sequence: upDownState.sequence,
    estimatedDurationSec: 120,
    focus: "scale",
    goal: "Dominar la escala en ambos sentidos",
    tip: "Al bajar, presta atención a los mismos patrones que al subir, pero al revés.",
  });

  // 3. Pentatónica
  const pentaDef = EXERCISES.find((e) => e.id === "pentatonic")!;
  const pentaState = pentaDef.generate();
  exercises.push({
    stepId: "pentatonic",
    title: "🎸 Pentatónica",
    description: "La escala de 5 notas del rock",
    exerciseDef: pentaDef,
    exerciseState: pentaState,
    sequence: pentaState.sequence,
    estimatedDurationSec: 120,
    focus: "scale",
    goal: "Aprender la pentatónica, base de los solos",
    tip: "Con solo 5 notas puedes improvisar. ¡Pruébalo!",
  });

  // 4. CAGED: visualizar patrones
  if (cagedPatterns.length > 0) {
    const bestPattern = cagedPatterns[0];
    exercises.push({
      stepId: "caged-visualize",
      title: `📐 Patrón CAGED: ${bestPattern.shapeName}`,
      description: `Visualiza el patrón ${bestPattern.shapeName} para ${root}`,
      exerciseDef: EXERCISES.find((e) => e.id === "scale-climb")!,
      exerciseState: {
        exerciseId: "caged-pattern",
        title: `Patrón ${bestPattern.shapeName} - ${root} ${scaleType}`,
        instructions: [
          `Este es el patrón ${bestPattern.shapeName}. La tónica está en las cuerdas ${bestPattern.tonicStrings.join(" y ")}.`,
          "Observa cómo se distribuyen las notas en esta ventana de 5 trastes.",
          `La tónica (${root}) es la nota raíz.`,
        ],
        neckPositions: bestPattern.positions,
        tips: [
          `El patrón ${bestPattern.shapeName} es uno de los 5 del sistema CAGED.`,
          `Cubre del traste ${bestPattern.lowestFret} al ${bestPattern.highestFret}.`,
        ],
        stars: 3,
        progressTotal: 1,
      },
      estimatedDurationSec: 90,
      focus: "caged",
      goal: "Reconocer visualmente el patrón CAGED en el mástil",
      tip: "Cada patrón CAGED tiene la tónica en cuerdas específicas. Aprende eso primero.",
    });
  }

  // 5. Conexiones entre patrones
  if (connections.length > 0) {
    const firstConn = connections[0];
    exercises.push({
      stepId: "caged-connection",
      title: "🔗 Conectando patrones",
      description: `${firstConn.fromShape} → ${firstConn.toShape}: aprende a conectar`,
      exerciseDef: EXERCISES.find((e) => e.id === "scale-climb")!,
      exerciseState: {
        exerciseId: "caged-connection",
        title: `Conectar ${firstConn.fromShape} → ${firstConn.toShape}`,
        instructions: [
          `El patrón ${firstConn.fromShape} se conecta con ${firstConn.toShape} por la cuerda ${firstConn.sharedString}.`,
          "Busca la tónica en esa cuerda compartida.",
          "Desde ahí, puedes saltar al siguiente patrón.",
        ],
        neckPositions: cagedPatterns.flatMap((p) => p.positions),
        tips: [
          `La conexión ${firstConn.fromShape}→${firstConn.toShape} usa la cuerda ${firstConn.sharedString}.`,
          "Aprender las conexiones te permite navegar todo el mástil.",
        ],
        stars: 3,
        progressTotal: 1,
      },
      estimatedDurationSec: 120,
      focus: "caged",
      goal: "Entender cómo se conectan los patrones CAGED",
      tip: "El sistema CAGED es como un rompecabezas: cada pieza conecta con la siguiente.",
    });
  }

  return {
    id: "intermediate-caged",
    title: `🎸 ${root} — Exploración CAGED`,
    description: `Practica ${root} Mayor con escalas y patrones CAGED`,
    level: "intermedio",
    exercises,
    totalMinutes: Math.ceil(
      exercises.reduce((sum, e) => sum + e.estimatedDurationSec, 0) / 60
    ),
    emoji: "🎸",
    closingTip:
      "¡Sigue conectando! El mástil completo se vuelve familiar cuando entiendes CAGED.",
  };
}

/**
 * Genera un plan desde una tarea de Firebase.
 * Toma la configuración de la tarea y construye ejercicios.
 */
export function generateSessionFromTaskConfig(
  config: Record<string, any>,
  type: string
): PracticeSessionPlan {
  const exercises: SessionExercise[] = [];

  if (type === "scale" && config.rootNote) {
    const patterns = generateCagedPatterns(
      config.rootNote as NoteName,
      config.scaleType || "major"
    );
    const scalePattern = buildScalePattern(
      config.rootNote as NoteName,
      config.scaleType || "major"
    );

    exercises.push({
      stepId: "scale-visualize",
      title: `🎵 Escala de ${config.rootNote}`,
      description: `Visualiza la escala ${config.scaleType || "Mayor"} de ${config.rootNote} en el mástil`,
      exerciseDef: EXERCISES.find((e) => e.id === "scale-climb")!,
      exerciseState: {
        exerciseId: "task-scale",
        title: `Escala de ${config.rootNote} ${config.scaleType || "Mayor"}`,
        instructions: [
          `Esta es la escala de ${config.rootNote} ${config.scaleType || "Mayor"}.`,
          "Toca cada nota en orden, siguiendo la secuencia.",
          "Concéntrate en la digitación correcta.",
        ],
        neckPositions: scalePattern.positions,
        tips: [
          "Practica lento al principio. La precisión es más importante que la velocidad.",
        ],
        stars: 3,
        progressTotal: 1,
      },
      estimatedDurationSec: 180,
      focus: "scale",
      goal: "Aprender la escala asignada por el profesor",
      tip: "Usa el metrónomo para mantener el ritmo constante.",
    });

    if (patterns.length > 0) {
      exercises.push({
        stepId: "caged-task-pattern",
        title: `📐 Patrón CAGED para ${config.rootNote}`,
        description: "Visualiza los patrones CAGED de esta escala",
        exerciseDef: EXERCISES.find((e) => e.id === "scale-climb")!,
        exerciseState: {
          exerciseId: "task-caged",
          title: `Patrones CAGED - ${config.rootNote}`,
          instructions: [
            "Los patrones CAGED son 5 formas de tocar la misma escala.",
            "Cada uno tiene la tónica en cuerdas diferentes.",
            "Observa cómo se conectan entre sí.",
          ],
          neckPositions: patterns.flatMap((p) =>
            p.positions.map((pos) => ({
              ...pos,
              highlight: pos.highlight === "root" ? "root" : "scale",
            }))
          ),
          tips: [
            "Los 5 patrones son: E, D, C, A, G.",
            "Aprende a conectar un patrón con el siguiente.",
          ],
          stars: 3,
          progressTotal: patterns.length,
        },
        estimatedDurationSec: 300,
        focus: "caged",
        goal: "Entender cómo los patrones CAGED cubren el mástil",
        tip: "Cada patrón CAGED se conecta con el vecino por una tónica compartida.",
      });
    }
  } else if (type === "finger" && config.exerciseId) {
    const def = EXERCISES.find((e) => e.id === config.exerciseId);
    if (def) {
      const state = def.generate();
      exercises.push({
        stepId: "finger-task",
        title: config.exerciseTitle || "Ejercicio de técnica",
        description: def.description,
        exerciseDef: def,
        exerciseState: state,
        sequence: state.sequence,
        estimatedDurationSec: 180,
        focus: "technique",
        goal: def.description,
        tip: state.tips?.[0] || "Concéntrate en la precisión.",
      });
    }
  } else if (type === "chord" && config.root) {
    const scalePattern = buildScalePattern(
      config.root as NoteName,
      config.quality === "minor" ? "minor" : "major"
    );
    exercises.push({
      stepId: "chord-task",
      title: `🎹 Acorde de ${config.root} ${config.quality || "Mayor"}`,
      description: "Visualiza y practica el acorde en el mástil",
      exerciseDef: EXERCISES.find((e) => e.id === "chord-notes")!,
      exerciseState: {
        exerciseId: "task-chord",
        title: `${config.root} ${config.quality || "Mayor"}`,
        instructions: [
          "Esta es la forma del acorde en el mástil.",
          "Identifica la raíz, la tercera y la quinta.",
          "Practica la transición entre posiciones.",
        ],
        neckPositions: scalePattern.positions.filter((p) =>
          [0, 4, 7].includes(p.interval ?? -1)
        ),
        tips: ["Los acordes Mayores tienen: Raíz + Tercera + Quinta."],
        stars: 3,
        progressTotal: 1,
      },
      estimatedDurationSec: 180,
      focus: "chord",
      goal: "Aprender la estructura del acorde Mayor",
      tip: "Cada acorde tiene 3 notas esenciales. Encuéntralas en distintas partes del mástil.",
    });
  } else if (type === "note" && config.targetNote) {
    const def = EXERCISES.find((e) => e.id === "find-note")!;
    const state = def.generate();
    exercises.push({
      stepId: "note-task",
      title: `🔍 Encuentra ${config.targetNote}`,
      description: "Busca la nota asignada por tu profesor",
      exerciseDef: def,
      exerciseState: state,
      estimatedDurationSec: 120,
      focus: "note",
      goal: "Identificar la nota en todo el mástil",
      tip: "Usa las cuerdas al aire como referencia.",
    });
  }

  // Si no hay ejercicios, poner uno por defecto
  if (exercises.length === 0) {
    const def = EXERCISES[0];
    const state = def.generate();
    exercises.push({
      stepId: "default",
      title: def.title,
      description: def.description,
      exerciseDef: def,
      exerciseState: state,
      sequence: state.sequence,
      estimatedDurationSec: 120,
      focus: "technique",
      goal: def.description,
      tip: state.tips?.[0] || "¡Tú puedes!",
    });
  }

  return {
    id: `task-${Date.now()}`,
    title: `📋 Tarea de ${type}`,
    description: "Práctica asignada por tu profesor",
    level: "intermedio",
    exercises,
    totalMinutes: Math.ceil(
      exercises.reduce((sum, e) => sum + e.estimatedDurationSec, 0) / 60
    ),
    emoji: "📋",
    closingTip:
      "¡Completaste la tarea! Tu profesor recibirá tus resultados.",
  };
}

// ─── Estados de la sesión ───

export type SessionStatus =
  | "idle"        // No hay sesión cargada
  | "ready"       // Sesión cargada, esperando empezar
  | "in-progress" // Ejercicio en curso
  | "paused"      // Pausado
  | "completed";  // Todos los ejercicios terminados

export type ExerciseStatus =
  | "pending"     // No empezado
  | "active"      // En curso ahora
  | "completed"   // Terminado
  | "skipped";    // Saltado

export interface SessionUIState {
  status: SessionStatus;
  plan: PracticeSessionPlan | null;
  progress: SessionProgress | null;
  currentExerciseIndex: number;
  currentExercise: SessionExercise | null;
  exerciseStatus: ExerciseStatus;
  feedback: CheckResult | null;
}
